import { useState } from 'react';
import { formatDistance } from '../game/geo';
import { GAME_MAPS, getGameMap } from '../game/maps';
import { formatScore, MAX_GAME_SCORE } from '../game/scoring';
import { averageScore, type PlayerStats } from '../game/stats';
import { getProvider, isProviderId } from '../providers';
import { IconChart, IconTrash } from './Icons';

interface StatsPanelProps {
  stats: PlayerStats;
  onClear: () => void;
}

const dateFormat = new Intl.DateTimeFormat('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });

export default function StatsPanel({ stats, onClear }: StatsPanelProps) {
  const [confirming, setConfirming] = useState(false);

  return (
    <section className="panel stats-panel" aria-labelledby="stats-title">
      <h2 id="stats-title" className="panel__title">
        <IconChart size={18} /> Vos statistiques
      </h2>
      {stats.gamesPlayed === 0 ? (
        <p className="muted">Aucune partie terminée pour l’instant. Vos scores seront enregistrés sur cet appareil.</p>
      ) : (
        <>
          <dl className="stat-grid">
            <div>
              <dt>Meilleur score</dt>
              <dd>{formatScore(stats.bestGame?.score ?? 0)}</dd>
            </div>
            <div>
              <dt>Parties</dt>
              <dd>{formatScore(stats.gamesPlayed)}</dd>
            </div>
            <div>
              <dt>Score moyen</dt>
              <dd>{formatScore(averageScore(stats))}</dd>
            </div>
            <div>
              <dt>Manches parfaites</dt>
              <dd>{formatScore(stats.perfectRounds)}</dd>
            </div>
            <div className="stat-grid__wide">
              <dt>Réponse la plus proche</dt>
              <dd>{stats.closestGuessKm === null ? '—' : formatDistance(stats.closestGuessKm)}</dd>
            </div>
          </dl>

          <h3 className="panel__subtitle">Records par carte</h3>
          <ul className="records">
            {GAME_MAPS.map((m) => (
              <li key={m.id}>
                <span>{m.name}</span>
                <span className="records__bar" aria-hidden="true">
                  <span style={{ width: `${((stats.bestByMap[m.id] ?? 0) / MAX_GAME_SCORE) * 100}%` }} />
                </span>
                <strong>{stats.bestByMap[m.id] !== undefined ? formatScore(stats.bestByMap[m.id] as number) : '—'}</strong>
              </li>
            ))}
          </ul>

          <h3 className="panel__subtitle">Dernières parties</h3>
          <ul className="history">
            {stats.history.slice(0, 6).map((g) => (
              <li key={g.id}>
                <span className="history__date">{dateFormat.format(new Date(g.date))}</span>
                <span className="history__map">
                  {getGameMap(g.mapId).name} · {isProviderId(g.providerId) ? getProvider(g.providerId).name : g.providerId}
                </span>
                <strong>{formatScore(g.score)}</strong>
              </li>
            ))}
          </ul>

          <div className="stats-panel__clear">
            {confirming ? (
              <>
                <span>Effacer toutes les statistiques ?</span>
                <button
                  type="button"
                  className="btn btn--danger btn--sm"
                  onClick={() => {
                    onClear();
                    setConfirming(false);
                  }}
                >
                  Oui, effacer
                </button>
                <button type="button" className="btn btn--ghost btn--sm" onClick={() => setConfirming(false)}>
                  Annuler
                </button>
              </>
            ) : (
              <button type="button" className="btn btn--ghost btn--sm" onClick={() => setConfirming(true)}>
                <IconTrash size={16} /> Réinitialiser
              </button>
            )}
          </div>
        </>
      )}
    </section>
  );
}
